import * as React from "react";
import { Check, Copy, RefreshCw, ThumbsDown, ThumbsUp } from "lucide-react";

import { useCopyFeedback } from "./lib/copy";
import { formatTime } from "./lib/date";
import { cn } from "./lib/utils";
import { Alert } from "./ui/alert";
import { Avatar } from "./ui/avatar";
import { Button } from "./ui/button";
import { Disclosure } from "./ui/disclosure";
import { IconButton } from "./ui/icon-button";
import { LIST_ROW_ACTIONS_REVEAL } from "./ui/list-item";
import {
  StepIcon,
  Steps,
  StepsStatus,
  settleSteps,
  stepsSummaryState,
  type StepItem,
  type StepsLabels,
  type StepsOutcome,
} from "./ui/steps";
import { Textarea } from "./ui/textarea";
import { Throbber } from "./ui/throbber";

/**
 * v2 chat message parts (redesign phase 5, #455; breaks #133-#136, #139).
 * Presentational only: markdown, citations, streaming, feedback mutations,
 * attachment uploads and which message's choices are active stay in the app
 * (swap recipe: docs/design-library/04-implementation-notes.md, "Chat").
 * Storybook only until implementation.
 *
 * - ChatUserMessage: a `--muted` bubble with `--ink` text, right-aligned, max
 *   80 %. Blue means action (principle 1), so the bubble is not blue.
 *   `layout="row"` shows the bubble alone: no name, avatar or time (a chat is
 *   always the reader and lytra, and the answer carries the time).
 * - ChatAssistantMessage: no bubble. Avatar sm + "lytra" + time on a group's
 *   first message. No AI marker per message: the marker labels the chat panel
 *   once (one per AI region, AI marker rules in 04).
 * - ChatSteps: the "Arbeitsschritte" summary line (Disclosure + StepsStatus + Steps sm).
 * - ChatChoices: outline Buttons sm in a wrap row; never blue (the composer's
 *   send is the one blue action).
 *
 * Kopieren copies answer text only: it shows on `state="ready"`, never on a
 * status. A turn the user stopped is `state="stopped"`: what was written so far
 * stays, `labels.stopped` ("Antwort gestoppt.") follows as a status line, and
 * the only action is Neu generieren (break #186; ChatSteps takes
 * `outcome="stopped"` for the same turn).
 *
 * Every message is an `<article>` named "<author>, <time>". Inside a
 * ChatThread, an assistant message's `continued` (a later message of the same
 * group: no header) comes from context; the prop overrides it.
 */

export type ChatSize = "md" | "sm";

// ── Grouping context ────────────────────────────────────────────────────────

export interface ChatGroupContextValue {
  /** A later message of the same author's group: no assistant header, tighter gap. */
  continued: boolean;
}

export const ChatGroupContext = React.createContext<ChatGroupContextValue>({ continued: false });

function MessageName({ id, author, time, className }: { id: string; author: string; time?: Date; className?: string }) {
  return (
    <span id={id} className={className}>
      {author}
      {time && <span className="sr-only">, {formatTime(time)}</span>}
    </span>
  );
}

function Time({ time, className }: { time: Date; className?: string }) {
  // aria-hidden: the time is already part of the article's name.
  return (
    <time aria-hidden="true" dateTime={time.toISOString()} className={cn("font-sans text-caption text-ink-muted tabular-nums", className)}>
      {formatTime(time)}
    </time>
  );
}

// ── User message ────────────────────────────────────────────────────────────

export interface ChatUserMessageLabels {
  /** The author's name for screen readers ("Sie"); the bubble shows none. */
  author: string;
}

export interface ChatUserMessageProps extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  text?: string;
  /** Replaces `text` (rich content); line breaks are then the caller's. */
  children?: React.ReactNode;
  time?: Date;
  /** Identifiers and attachments: a ChipGroup (pass `className="justify-end"`). */
  chips?: React.ReactNode;
  /**
   * `bubble` (default): right-aligned `--muted` bubble, time under it.
   * `row`: the bubble alone. A chat is always the reader and lytra, and the
   * answer that follows right away carries the time, so the user's side shows no
   * name, avatar or time; name and time stay for screen readers.
   */
  layout?: "bubble" | "row";
  labels: ChatUserMessageLabels;
}

export const ChatUserMessage = React.forwardRef<HTMLElement, ChatUserMessageProps>(function ChatUserMessage(
  { text, children, time, chips, layout = "bubble", labels, className, ...props },
  ref,
) {
  const nameId = React.useId();
  if (layout === "row") {
    return (
      <article
        ref={ref}
        aria-labelledby={nameId}
        data-slot="chat-message"
        data-author="user"
        className={cn("flex flex-col items-end gap-2", className)}
        {...props}
      >
        <MessageName id={nameId} author={labels.author} time={time} className="sr-only" />
        <div
          className={cn(
            "max-w-4/5 rounded-xl bg-muted px-3 py-2 font-body text-body-long text-ink wrap-break-word",
            children == null && "whitespace-pre-wrap",
          )}
        >
          {children ?? text}
        </div>
        {chips}
      </article>
    );
  }
  return (
    <article
      ref={ref}
      aria-labelledby={nameId}
      data-slot="chat-message"
      data-author="user"
      className={cn("flex flex-col items-end gap-1", className)}
      {...props}
    >
      <MessageName id={nameId} author={labels.author} time={time} className="sr-only" />
      <div
        className={cn(
          "max-w-4/5 rounded-xl bg-muted px-3 py-2 font-body text-body text-ink wrap-break-word",
          children == null && "whitespace-pre-wrap",
        )}
      >
        {children ?? text}
      </div>
      {chips != null && <div className="flex max-w-full justify-end">{chips}</div>}
      {time && <Time time={time} />}
    </article>
  );
});

// ── Arbeitsschritte ─────────────────────────────────────────────────────────

export interface ChatStepsLabels {
  /** The summary line, computed by the app: "Arbeitet …", "3 Arbeitsschritte · 1 fehlgeschlagen". */
  summary: React.ReactNode;
  /** The list's name ("Arbeitsschritte"). */
  list: string;
  /** Hidden state words; German defaults from Steps. */
  states?: Partial<StepsLabels>;
}

export interface ChatStepsProps {
  steps: readonly StepItem[];
  /**
   * The turn's outcome: the summary icon follows it (red only for a failed turn, break #121).
   * `stopped`: steps still running or pending show as skipped, the icon is a neutral stop (break #185).
   */
  outcome?: StepsOutcome;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  labels: ChatStepsLabels;
  className?: string;
}

/**
 * The round-7 composition: one StepsStatus outside the Disclosure (which
 * unmounts its content when closed), the list inside with `announce={false}`.
 */
export function ChatSteps({ steps: given, outcome, open, defaultOpen, onOpenChange, labels, className }: ChatStepsProps) {
  const steps = settleSteps(given, outcome);
  const summaryState = stepsSummaryState(
    steps.map((s) => s.state),
    outcome,
  );
  return (
    <div data-slot="chat-steps" className={className}>
      <StepsStatus steps={steps} labels={labels.states} />
      <Disclosure
        size="sm"
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        title={
          <span className="flex items-center gap-1.5 tabular-nums">
            <StepIcon state={summaryState} size={12} />
            {labels.summary}
          </span>
        }
      >
        <Steps size="sm" steps={steps} announce={false} labels={labels.states} aria-label={labels.list} />
      </Disclosure>
    </div>
  );
}

// ── Choices ─────────────────────────────────────────────────────────────────

export interface ChatChoicesProps {
  /** Each option is its own value: the text that gets sent. */
  options: readonly string[];
  /** The click event comes along for hosts that need the point (fact-fly origin). */
  onSelect: (value: string, event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  labels: { group: string };
  className?: string;
}

export function ChatChoices({ options, onSelect, disabled = false, labels, className }: ChatChoicesProps) {
  return (
    <div role="group" aria-label={labels.group} className={cn("flex flex-wrap gap-2", className)}>
      {options.map((option) => (
        <Button
          key={option}
          variant="outline"
          size="sm"
          disabled={disabled}
          // Options are generated sentences: they wrap instead of overflowing a 360 panel.
          className="h-auto min-h-7 max-w-full py-1 text-left whitespace-normal"
          onClick={(event) => onSelect(option, event)}
        >
          {option}
        </Button>
      ))}
    </div>
  );
}

// ── Assistant message ───────────────────────────────────────────────────────

export type ChatAssistantState = "waiting" | "streaming" | "ready" | "stopped" | "error";

/** 1 = helpful, -1 = not helpful. */
export type ChatRating = 1 | -1;
export type ChatFeedbackValue = ChatRating | null;

/** The reader's own rating of an answer and why. A thumb saves the rating at once, with
 * the saved comment; the comment box it opens is optional. */
export interface ChatFeedback {
  /** The saved rating; null before the reader rated. */
  value: ChatFeedbackValue;
  /** The saved comment. */
  comment?: string | null;
  onSave: (rating: ChatRating, comment: string) => void;
}

export interface ChatAssistantMessageLabels {
  author: string;
  /** Waiting line: "Denkt nach …". */
  thinking: string;
  copy: string;
  copied: string;
  copyFailed: string;
  /** Retry on a finished answer: "Neu generieren". */
  regenerate: string;
  /** Error text: "Die Antwort konnte nicht erstellt werden." */
  error: string;
  /** Status line of a turn the user stopped: "Antwort gestoppt." Never copied. */
  stopped: string;
  /** Retry in the error: "Erneut versuchen". */
  retry: string;
  helpful: string;
  notHelpful: string;
  /** The optional comment box after a thumb: "Was war gut oder falsch? (optional)". */
  feedbackComment: string;
  saveFeedbackComment: string;
  cancelFeedbackComment: string;
}

export interface ChatAssistantMessageProps extends Omit<React.HTMLAttributes<HTMLElement>, "children" | "onCopy"> {
  state?: ChatAssistantState;
  /** Plain text (line breaks kept). Also what Kopieren copies unless `copyText` is set. */
  text?: string;
  /** Rich content (the app's rendered markdown); replaces `text` on screen. */
  children?: React.ReactNode;
  copyText?: string;
  time?: Date;
  /** The logo; without it the Avatar shows the author's initial. */
  avatarSrc?: string;
  /** Replaces the Avatar, e.g. the brand mark drawn as a vector. */
  avatar?: React.ReactNode;
  /** ChatSteps above the text. */
  steps?: React.ReactNode;
  /** ChatSteps for tool calls after the text started. */
  stepsAfter?: React.ReactNode;
  /** Source list under the text (the app's MessageReferences). */
  sources?: React.ReactNode;
  /** ChatChoices. */
  choices?: React.ReactNode;
  /** Replaces the clipboard write; a throw or rejection shows `copyFailed`. */
  onCopy?: (text: string) => void | Promise<void>;
  /** "Neu generieren" on a finished answer, "Erneut versuchen" in the error. */
  onRetry?: () => void;
  feedback?: ChatFeedback;
  extraActions?: React.ReactNode;
  /** Show the actions at rest (the latest answer); otherwise they appear on hover and focus. */
  actionsVisible?: boolean;
  continued?: boolean;
  size?: ChatSize;
  labels: ChatAssistantMessageLabels;
}

export const ChatAssistantMessage = React.forwardRef<HTMLElement, ChatAssistantMessageProps>(function ChatAssistantMessage(
  {
    state = "ready",
    text,
    children,
    copyText,
    time,
    avatarSrc,
    avatar,
    steps,
    stepsAfter,
    sources,
    choices,
    onCopy,
    onRetry,
    feedback,
    extraActions,
    actionsVisible = false,
    continued: continuedProp,
    size = "md",
    labels,
    className,
    ...props
  },
  ref,
) {
  const nameId = React.useId();
  const groupContinued = React.useContext(ChatGroupContext).continued;
  const continued = continuedProp ?? groupContinued;
  const sm = size === "sm";
  const { state: copyState, copy } = useCopyFeedback(onCopy);
  // Only while the comment box is open: the rating just saved and the comment being written.
  const [draft, setDraft] = React.useState<{ rating: ChatRating; comment: string } | null>(null);
  const rating = draft?.rating ?? feedback?.value ?? null;

  const hasBody = children != null || !!text;
  const waiting = (state === "waiting" || (state === "streaming" && !hasBody)) && steps == null;
  const showBody = hasBody && state !== "error";
  // Only a finished answer is copied or rated; a stopped turn offers Neu generieren alone.
  const ready = state === "ready";
  const toCopy = ready ? (copyText ?? text) : undefined;
  const showActions =
    (ready && (toCopy != null || onRetry != null || feedback != null || extraActions != null)) ||
    (state === "stopped" && onRetry != null);
  const pinned = actionsVisible || (feedback != null && rating != null);
  const actionSize = sm ? "xs" : "sm";

  const copyLabel = copyState === "copied" ? labels.copied : copyState === "failed" ? labels.copyFailed : labels.copy;
  const copyStatus = copyState === "copied" ? labels.copied : copyState === "failed" ? labels.copyFailed : "";

  const thumb = (value: ChatRating) =>
    feedback && (
      <IconButton
        icon={value === 1 ? ThumbsUp : ThumbsDown}
        label={value === 1 ? labels.helpful : labels.notHelpful}
        size={actionSize}
        aria-pressed={rating === value}
        // Pressed: filled, green for helpful and red for not helpful.
        className={cn(
          "aria-pressed:[&_svg]:fill-current",
          value === 1 ? "aria-pressed:text-success-strong" : "aria-pressed:text-destructive",
        )}
        onClick={() => {
          // The saved comment goes with the new rating: a thumb alone must not erase it.
          if (value !== feedback.value) feedback.onSave(value, feedback.comment ?? "");
          setDraft({ rating: value, comment: draft?.comment ?? feedback.comment ?? "" });
        }}
      />
    );

  return (
    <article
      ref={ref}
      aria-labelledby={nameId}
      data-slot="chat-message"
      data-author="assistant"
      data-state={state}
      className={cn("group/item flex gap-2", className)}
      {...props}
    >
      {/* The avatar column stays on continued messages, so text lines up across the group. */}
      <div className="w-6 shrink-0">{!continued && (avatar ?? <Avatar size="sm" name={labels.author} src={avatarSrc} />)}</div>
      <div className={cn("flex min-w-0 flex-1 flex-col", sm ? "gap-1.5" : "gap-2")}>
        {continued ? (
          <MessageName id={nameId} author={labels.author} time={time} className="sr-only" />
        ) : (
          <p className="flex h-6 items-center gap-2">
            <MessageName id={nameId} author={labels.author} time={time} className="font-sans text-caption font-semibold text-ink" />
            {time && <Time time={time} />}
          </p>
        )}

        {steps}

        {waiting && (
          <p role="status" className="flex items-center gap-1.5 font-sans text-caption text-ink-muted">
            <Throbber decorative size={sm ? 12 : 16} />
            {labels.thinking}
          </p>
        )}

        {showBody && (
          <div
            data-slot="chat-message-body"
            aria-busy={state === "streaming" || undefined}
            className={cn(
              "font-body text-ink wrap-break-word",
              sm ? "text-body" : "text-body-long",
              children == null && "whitespace-pre-wrap",
            )}
          >
            {children ?? text}
          </div>
        )}

        {state === "error" && (
          <Alert
            tone="error"
            actions={
              onRetry && (
                <Button size="sm" variant="outline" leadingIcon={RefreshCw} onClick={onRetry}>
                  {labels.retry}
                </Button>
              )
            }
          >
            {labels.error}
          </Alert>
        )}

        {state === "stopped" && (
          <p data-slot="chat-message-status" className="font-sans text-caption text-ink-muted">
            {labels.stopped}
          </p>
        )}

        {stepsAfter}
        {state !== "error" && sources}
        {state !== "error" && choices}

        {showActions && (
          <div
            data-slot="chat-message-actions"
            className={cn("-ml-1 flex flex-wrap items-center gap-0.5", !pinned && LIST_ROW_ACTIONS_REVEAL)}
          >
            {toCopy != null && (
              <IconButton
                icon={copyState === "copied" ? Check : Copy}
                label={copyLabel}
                size={actionSize}
                onClick={() => void copy(toCopy)}
              />
            )}
            {onRetry && <IconButton icon={RefreshCw} label={labels.regenerate} size={actionSize} onClick={onRetry} />}
            {ready && thumb(1)}
            {ready && thumb(-1)}
            {ready && extraActions}
          </div>
        )}
        {ready && feedback && draft && (
          <div data-slot="chat-message-feedback" className="flex max-w-xl flex-col gap-2">
            <Textarea
              autoFocus
              rows={2}
              aria-label={labels.feedbackComment}
              placeholder={labels.feedbackComment}
              value={draft.comment}
              onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                {labels.cancelFeedbackComment}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  feedback.onSave(draft.rating, draft.comment);
                  setDraft(null);
                }}
              >
                {labels.saveFeedbackComment}
              </Button>
            </div>
          </div>
        )}
        {ready && feedback?.comment && !draft && (
          <p data-slot="chat-message-feedback" className="whitespace-pre-wrap font-sans text-caption text-ink-muted">
            {feedback.comment}
          </p>
        )}
        {/* Copy feedback for screen readers; always mounted so the change is announced. */}
        {toCopy != null && (
          <span role="status" className="sr-only" data-slot="copy-status">
            {copyStatus}
          </span>
        )}
      </div>
    </article>
  );
});
